/**
 * Lenavio AI Governance — audit-log API client.
 *
 * Mirrors ``backend/app/routers/ai_audit.py``. All endpoints are
 * gated server-side on ``audit_logs`` (Enterprise/trialing) +
 * HR_ADMIN+; the frontend uses ``usePlanFeatures().has("audit_logs")``
 * to hide the tab from lower tiers rather than letting them hit a 402.
 */
import client from "./client";

export interface AIAuditRow {
  id: string;
  org_id: string;
  user_id: string | null;
  user_name: string | null;
  feature: string;
  model_used: string;
  input_summary: string | null;
  output_summary: string | null;
  tokens_input: number;
  tokens_output: number;
  cost_estimate_usd: number;
  duration_ms: number;
  status: string;
  error_message: string | null;
  human_reviewed: boolean;
  reviewed_by: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  created_at: string;
}

export interface AIAuditPage {
  rows: AIAuditRow[];
  total: number;
  page: number;
  per_page: number;
}

export interface AIAuditFeatureStat {
  feature: string;
  call_count: number;
  tokens_total: number;
  cost_usd: number;
  error_count: number;
}

export interface AIAuditSummary {
  window_days: number;
  total_calls: number;
  total_tokens: number;
  total_cost_usd: number;
  error_rate_pct: number;
  human_reviewed_pct: number;
  by_feature: AIAuditFeatureStat[];
}

export interface AIAuditFilters {
  feature?: string;
  status?: string;
  user_id?: string;
  since?: string;
  until?: string;
  reviewed?: boolean;
  page?: number;
  per_page?: number;
}

export async function listAIAudit(
  filters: AIAuditFilters = {},
): Promise<AIAuditPage> {
  const params: Record<string, string | number | boolean> = {
    page: filters.page ?? 1,
    per_page: filters.per_page ?? 25,
  };
  if (filters.feature) params.feature = filters.feature;
  if (filters.status) params.status = filters.status;
  if (filters.user_id) params.user_id = filters.user_id;
  if (filters.since) params.since = filters.since;
  if (filters.until) params.until = filters.until;
  if (filters.reviewed !== undefined) params.reviewed = filters.reviewed;
  const res = await client.get<AIAuditPage>("/ai-audit", { params });
  return res.data;
}

export async function fetchAIAuditSummary(days = 30): Promise<AIAuditSummary> {
  const res = await client.get<AIAuditSummary>("/ai-audit/summary", {
    params: { days },
  });
  return res.data;
}

export async function markAIAuditReviewed(
  id: string,
  reviewed = true,
): Promise<{
  id: string;
  human_reviewed: boolean;
  reviewed_by: string | null;
  reviewed_at: string | null;
}> {
  const res = await client.put(`/ai-audit/${id}/review`, { reviewed });
  return res.data;
}
